import { useCallback } from 'react';

interface UseTimelineKeyboardNavigationProps {
  mode: string;
  hasFocus: boolean;
  flipLayout?: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onFirst: () => void;
  onLast: () => void;
}

/**
 * Hook for handling timeline keyboard navigation
 */
export const useTimelineKeyboardNavigation = ({
  mode,
  hasFocus,
  flipLayout = false,
  onNext,
  onPrevious,
  onFirst,
  onLast,
}: UseTimelineKeyboardNavigationProps) => {
  const handleKeySelection = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!hasFocus) return;

      const { key } = event;

      // Don't handle timeline navigation if event is from popover menu
      // Popover menus (ARIA menu pattern) handle their own arrow keys
      const target = event.target as HTMLElement;
      if (
        target.closest('[role="menu"]') ||
        target.closest('[aria-haspopup="dialog"]')
      ) {
        return; // Let menu handle its own navigation
      }

      // Common handlers
      switch (key) {
        case 'Home':
          event.preventDefault();
          onFirst();
          return;
        case 'End':
          event.preventDefault();
          onLast();
          return;
      }

      // Mode-specific handlers
      if (mode === 'HORIZONTAL' || mode === 'HORIZONTAL_ALL') {
        if (key === 'ArrowRight') {
          event.preventDefault();
          flipLayout ? onPrevious() : onNext();
        } else if (key === 'ArrowLeft') {
          event.preventDefault();
          flipLayout ? onNext() : onPrevious();
        }
      } else if (mode === 'VERTICAL' || mode === 'VERTICAL_ALTERNATING') {
        if (key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
          onNext();
        } else if (key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          onPrevious();
        }
      }
    },
    [mode, flipLayout, hasFocus, onNext, onPrevious, onFirst, onLast],
  );

  return { handleKeySelection };
};
